'use client'

/**
 * /protests — Öffentliche Liste aller eingelegten Proteste (anonymisiert)
 *
 * Zeigt: Referenzcode (P-XXXXXXXX), Status, betroffene §§, Datum, Begründung der Jury.
 * Kein Pilot-Bezug, kein Flug-Detail, keine eigene Begründung, keine Anhänge.
 */

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { FglFooter } from '@/components/layout/fgl-footer'
import { useTranslation } from '@/i18n/client'
import {
  PUBLIC_STATUS_LABEL_DE,
  PUBLIC_STATUS_COLOR,
  type ProtestPublicStatus,
} from '@/lib/protest'

interface PublicProtest {
  referenceCode: string
  status: ProtestPublicStatus
  juryReason: string | null
  paragraphCount: number
  paragraphs: string[]
  submittedAt: string
  decidedAt: string | null
}

export default function PublicProtestsPage() {
  const { locale, t } = useTranslation()
  const isDe = locale === 'de'
  const [protests, setProtests] = useState<PublicProtest[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/public/protests')
      .then((r) => r.json())
      .then((data) => { setProtests(data.protests || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  return (
    <div data-award="true" className="min-h-screen" style={{ background: 'var(--award-ink)' }}>
      <FglHeader />

      <main className="pt-[84px]" style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px' }}>
        <header style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            {t('protests.eyebrow')}
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, lineHeight: 1.1, margin: 0 }}>
            {t('protests.titlePrefix')} <span style={{ color: 'var(--award-gold)' }}>{t('protests.titleAccent')}</span>
          </h1>
          <p style={{ marginTop: 16, opacity: 0.8, fontSize: 15, maxWidth: 720, lineHeight: 1.6 }}>
            {t('protests.intro')}{' '}
            <Link href="/wettbewerbsordnung#art-15" className="award-link" style={{ color: 'var(--award-gold)' }}>{t('protests.introLink')}</Link>.
          </p>
        </header>

        {!isDe && (
          <div className="award-legal-notice" style={{ marginBottom: 28 }}>
            <span className="award-legal-notice-flag" aria-hidden>§</span>
            <div className="award-legal-notice-body">
              <div className="award-legal-notice-de">
                Rechtlich verbindlich ist allein die deutsche Originalfassung. Begründungen der Jury bleiben im Original-Wortlaut.
              </div>
              <div className="award-legal-notice-tr">
                {t('legal.notice.localized')}
              </div>
            </div>
          </div>
        )}

        {loading && <div style={{ opacity: 0.6 }}>{t('protests.loading')}</div>}

        {!loading && protests.length === 0 && (
          <div style={{
            padding: 48, textAlign: 'center', background: 'rgba(255,255,255,0.04)',
            borderRadius: 20, opacity: 0.8, fontSize: 15,
          }}>
            {t('protests.empty')}
          </div>
        )}

        {!loading && protests.length > 0 && (
          <>
            <div style={{ marginBottom: 16, fontSize: 13, opacity: 0.6 }}>
              {t('protests.countSummary', { count: protests.length })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {protests.map((p) => {
                const color = PUBLIC_STATUS_COLOR[p.status]
                return (
                  <article key={p.referenceCode} style={{
                    padding: 20, background: 'rgba(255,255,255,0.04)',
                    borderRadius: 16, border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: 13, opacity: 0.6 }}>
                          {p.referenceCode}
                        </div>
                        <div style={{ fontSize: 13, marginTop: 4, opacity: 0.8 }}>
                          {t('protests.labelSubmitted')}: {new Date(p.submittedAt).toLocaleDateString(locale, { dateStyle: 'long' })}
                          {p.decidedAt && <> · {t('protests.labelDecided')}: {new Date(p.decidedAt).toLocaleDateString(locale, { dateStyle: 'long' })}</>}
                        </div>
                      </div>
                      <span style={{
                        display: 'inline-block', background: color.bg, color: color.fg,
                        padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                      }}>
                        {PUBLIC_STATUS_LABEL_DE[p.status]}
                      </span>
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.7, marginBottom: p.juryReason ? 12 : 0 }}>
                      {t('protests.labelChallenged')}: <strong>{p.paragraphs.join(', ')}</strong>
                    </div>

                    {p.juryReason && (
                      <div style={{
                        padding: 12, background: 'rgba(0,0,0,0.25)',
                        borderRadius: 10, borderLeft: '3px solid var(--award-gold)',
                        fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                      }}>
                        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                          {t('protests.labelJuryReason')}
                        </div>
                        {p.juryReason}
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </>
        )}
      </main>

      <FglFooter />
    </div>
  )
}
